import { IconClockPause, IconLock } from "@tabler/icons-react";

import type { SecondFactor } from "../api/types";
import { SecondFactorSection } from "../components/account/SecondFactorSection";
import { GateCard } from "./GateCard";

/**
 * "Ask X" — or nothing at all when the deployment named nobody.
 *
 * The contact is served by `GET /api/v1/auth/config` (`SUPPORT_CONTACT`, or
 * the first `ADMIN_EMAILS` entry), because it is the one line a brand-new
 * person reads and a wrong one must be a configuration change rather than a
 * rebuilt image.
 *
 * Args:
 *     contact: Who to ask, when the server names somebody.
 *
 * Returns:
 *     The sentence to render.
 */
function AskFor({ contact }: { contact: string | null }): JSX.Element {
  if (!contact) {
    return (
      <>Ask whoever runs the Data Console — the person who sent you this link.</>
    );
  }
  return (
    <>
      Ask <span className="mono">{contact}</span>.
    </>
  );
}

interface PendingScreenProps {
  /** Who the server says you are; null when it would not even say that. */
  email: string | null;
  /** Who grants access, as the server publishes it. */
  accessContact: string | null;
  /** What the server says about this account's code, when it said anything. */
  secondFactor: SecondFactor | null;
  /** Extra sentence the server sent with its refusal. */
  serverMessage?: string | null;
  /** Ask the server again whether the account has been approved. */
  onCheckAgain: () => void;
  onSignOut: () => void;
}

/**
 * Signed in, and allowed to do exactly nothing yet.
 *
 * A first Google sign-in creates a `pending` account (`docs/AUTH.md` §2). This
 * is the whole console for that person until somebody approves them, so it has
 * to be complete rather than apologetic: who they are signed in as, what is
 * missing, who grants it, and the one thing they *can* do now — set up the
 * code the console will ask for later.
 *
 * Args:
 *     email: The signed-in address.
 *     accessContact: Who grants access, as the server names them.
 *     secondFactor: The account's second-factor state, when it is known.
 *     serverMessage: What the server said when it refused.
 *     onCheckAgain: Re-read the account.
 *     onSignOut: End the session.
 *
 * Returns:
 *     The waiting-for-approval screen.
 */
export function PendingScreen({
  email,
  accessContact,
  secondFactor,
  serverMessage = null,
  onCheckAgain,
  onSignOut,
}: PendingScreenProps): JSX.Element {
  return (
    <GateCard title="Your account is waiting for approval" wide>
      <p className="gate__badge">
        <IconClockPause size={14} aria-hidden="true" />
        Waiting for approval
      </p>

      <p className="gate__text">
        You are signed in{email ? " as " : ""}
        {email ? <span className="mono">{email}</span> : null}. Signing in is
        not access: somebody has to approve you before you can see a client's
        files, and that is a person's decision, not a delay.
      </p>

      <p className="gate__text">
        <AskFor contact={accessContact} /> Approval takes a few seconds once
        somebody reads it — you do not have to sign in again afterwards, this
        page picks it up.
      </p>

      {serverMessage ? <p className="gate__text">{serverMessage}</p> : null}

      <div className="gate__actions">
        <button type="button" className="btn" onClick={onCheckAgain}>
          Check again
        </button>
        <button type="button" className="btn btn--ghost" onClick={onSignOut}>
          Sign out
        </button>
      </div>

      {secondFactor ? (
        <div className="gate__block">
          <p className="gate__text">
            While you wait, you can set up your code. It is the only other thing
            this account may do today, and doing it now means the first time the
            console asks for it you are ready.
          </p>
          <SecondFactorSection secondFactor={secondFactor} />
        </div>
      ) : null}
    </GateCard>
  );
}

interface DisabledScreenProps {
  email: string | null;
  /** Who to ask about it, as the server publishes it. */
  accessContact: string | null;
  /** Why access was turned off, as the server worded it. */
  reason: string | null;
  onSignOut: () => void;
}

/**
 * Signed in, and switched off.
 *
 * Disabling is not a deletion (`docs/AUTH.md` §2): every record stays, and the
 * account can be approved again — so the screen says that, instead of leaving
 * somebody to assume something was destroyed.
 *
 * Args:
 *     email: The signed-in address.
 *     accessContact: Who to ask about it, as the server names them.
 *     reason: Why access was turned off.
 *     onSignOut: End the session.
 *
 * Returns:
 *     The access-turned-off screen.
 */
export function DisabledScreen({
  email,
  accessContact,
  reason,
  onSignOut,
}: DisabledScreenProps): JSX.Element {
  return (
    <GateCard title="Your access has been turned off">
      <p className="gate__badge gate__badge--danger">
        <IconLock size={14} aria-hidden="true" />
        Access turned off
      </p>

      <p className="gate__text">
        You are signed in{email ? " as " : ""}
        {email ? <span className="mono">{email}</span> : null}, but this account
        cannot open the catalogue or ask for a file.
      </p>

      {reason ? (
        <p className="gate__text">
          The reason recorded: <span className="gate__quote">{reason}</span>
        </p>
      ) : null}

      <p className="gate__text">
        Nothing has been deleted — every dataset, every consent and every trace
        is where it was, and the account can be turned back on. Any key you
        minted stops at its next request; a link already issued keeps working
        until it expires, because nothing revokes a URL that is already in
        someone's terminal. <AskFor contact={accessContact} />
      </p>

      <button type="button" className="btn" onClick={onSignOut}>
        Sign out
      </button>
    </GateCard>
  );
}
